import React, { useEffect, useState } from "react";
import {
  Button,
  Box,
  Menu,
  MenuItem,
  Stack,
  SvgIcon,
  Typography,
  Grid,
} from "@mui/material";
import SwapVertOutlinedIcon from "@mui/icons-material/SwapVertOutlined";

import { IconButton, Select, Tooltip } from "../../../../components";

import { ReactComponent as CloseIcon } from "../../../../assets/close.svg";
import { ReactComponent as DeleteIcon } from "../../../../assets/delete.svg";
import { ISortOption, TOrder, TSortableColumn } from ".";
import { TABLE_COLORS } from "../../../../constants";

interface IColumnSelectProps<T> {
  columns: TSortableColumn<T>[];
  value: keyof T;
  onChange: (value: keyof T) => void;
}

const ColumnSelect = <T,>({
  columns,
  value,
  onChange,
}: IColumnSelectProps<T>) => {
  return (
    <Select
      value={value as string}
      onChange={(e) => onChange(e.target.value as keyof T)}
      fullWidth
    >
      {columns.map((col, index) => (
        <MenuItem
          value={col.accessorKey as string}
          selected={col.accessorKey === value}
          key={index}
        >
          {col.header}
        </MenuItem>
      ))}
    </Select>
  );
};

interface IOrderSelectProps {
  value: TOrder;
  onChange: (value: TOrder) => void;
}

const OrderSelect = ({ value, onChange }: IOrderSelectProps) => {
  return (
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value as TOrder)}
      fullWidth
    >
      <MenuItem value="asc" selected={value === "asc"}>
        Ascending
      </MenuItem>
      <MenuItem value="desc" selected={value === "desc"}>
        Descending
      </MenuItem>
    </Select>
  );
};

interface ISortButtonProps<T> {
  columns: TSortableColumn<T>[];
  sortOptions: ISortOption<T>[];
  onChange?: (options: ISortOption<T>[]) => void;
}

export const SortButton = <T,>({
  columns,
  sortOptions: defaultSortOptions,
  onChange,
}: ISortButtonProps<T>) => {
  const [sortOptions, setSortOptions] =
    useState<ISortOption<T>[]>(defaultSortOptions);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);
  const visibleColumns = columns.filter((col) => !col.hidden);
  const buttonId = `sort-button-${columns.map((col) => col.header).join("-")}`;
  const menuId = `sort-menu-${columns.map((col) => col.header).join("-")}`;

  const handleClose = () => {
    setSortOptions(defaultSortOptions);
    setAnchorEl(null);
  };

  const handleDeleteSortOption = (option: ISortOption<T>) => {
    setSortOptions((prev) =>
      prev.filter((o) => o.accessorKey !== option.accessorKey),
    );
  };

  const handleColumnChange = (option: ISortOption<T>, value: keyof T) => {
    setSortOptions((prev) =>
      prev.map((o) =>
        o.accessorKey === option.accessorKey ? { ...o, accessorKey: value } : o,
      ),
    );
  };

  const handleOrderChange = (option: ISortOption<T>, value: TOrder) => {
    setSortOptions((prev) =>
      prev.map((o) =>
        o.accessorKey === option.accessorKey ? { ...o, order: value } : o,
      ),
    );
  };

  const handleAddOption = () => {
    const accessorKeys = sortOptions.map((o) => o.accessorKey);
    const column = columns.find(
      (col) => !accessorKeys.includes(col.accessorKey) && !col.hidden,
    );
    if (column) {
      setSortOptions((prev) => [
        ...prev,
        { accessorKey: column.accessorKey, order: "asc" },
      ]);
    }
  };

  const handleSort = () => {
    onChange?.(sortOptions);
    setAnchorEl(null);
  };

  useEffect(() => {
    setSortOptions(defaultSortOptions);
  }, [defaultSortOptions]);

  return (
    <>
      <Tooltip title={"Multiple Sort"}>
        <IconButton
          id={buttonId}
          aria-controls={isOpen ? menuId : undefined}
          aria-haspopup="true"
          aria-expanded={isOpen ? "true" : undefined}
          onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
            setAnchorEl(event.currentTarget)
          }
          size="large"
        >
          <SvgIcon component={SwapVertOutlinedIcon} sx={{ fontSize: "20px" }} />
        </IconButton>
      </Tooltip>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        slotProps={{
          list: { "aria-labelledby": buttonId },
        }}
      >
        <Stack width={"450px"} maxWidth={"100%"}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            borderBottom={`1px solid ${TABLE_COLORS.border}`}
            padding="8px 12px"
          >
            <Typography variant="h4">Multiple Sort</Typography>
            <SvgIcon
              component={CloseIcon}
              onClick={handleClose}
              sx={{ fontSize: "16px", cursor: "pointer" }}
              inheritViewBox
            />
          </Box>
          <Grid
            container
            rowSpacing="4px"
            columnSpacing="8px"
            padding="8px 12px"
          >
            {!!sortOptions.length && (
              <>
                <Grid size={3}></Grid>
                <Grid size={4}>
                  <Typography variant="subtitle1" align="center">
                    Column
                  </Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="subtitle1" align="center">
                    Direction
                  </Typography>
                </Grid>
                <Grid size={1}></Grid>
              </>
            )}
            {sortOptions.map((option, index) => (
              <React.Fragment key={index}>
                <Grid size={3} display="flex" alignItems={"center"}>
                  <Typography variant="subtitle1">
                    {index ? "Then By" : "Sort By"}
                  </Typography>
                </Grid>
                <Grid size={4}>
                  <ColumnSelect
                    columns={columns.filter(
                      (col) =>
                        !sortOptions.find(
                          (o) =>
                            col.accessorKey === o.accessorKey &&
                            o.accessorKey !== option.accessorKey,
                        ) && !col.hidden,
                    )}
                    value={option.accessorKey}
                    onChange={(value) => handleColumnChange(option, value)}
                  />
                </Grid>
                <Grid size={4} justifyContent={"center"}>
                  <OrderSelect
                    value={option.order}
                    onChange={(value) => handleOrderChange(option, value)}
                  />
                </Grid>
                <Grid size={1} display={"flex"} alignItems={"center"}>
                  {sortOptions.length > 1 && (
                    <SvgIcon
                      component={DeleteIcon}
                      onClick={() => handleDeleteSortOption(option)}
                      sx={{ fontSize: "20px", cursor: "pointer" }}
                      inheritViewBox
                    />
                  )}
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
          <Box display="flex" justifyContent="space-between" padding="8px 12px">
            <Button
              variant="outlined"
              disabled={sortOptions.length >= visibleColumns.length}
              onClick={handleAddOption}
            >
              Add Level
            </Button>
            <Button
              variant="contained"
              onClick={handleSort}
              disabled={!sortOptions.length}
            >
              Sort
            </Button>
          </Box>
        </Stack>
      </Menu>
    </>
  );
};

export default SortButton;
